import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', confirm: '', restaurantName: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'verify'

  const f = field => e => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    // Create the Supabase auth user
    const { data, error: signUpErr } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: { restaurant_name: form.restaurantName }
      }
    });

    if (signUpErr) {
      setLoading(false);
      setError(signUpErr.message);
      return;
    }

    // Try to link user to their restaurant by email match
    if (data.user) {
      const { data: restaurantId } = await supabase
        .rpc('link_user_to_restaurant', {
          p_user_id: data.user.id,
          p_email: form.email
        });

      if (!restaurantId) {
        console.warn('No restaurant found for email:', form.email);
        // Not a hard error — they land on the no-restaurant page
      }
    }

    setLoading(false);

    // If email confirmation is required, show verify step
    if (!data.session) {
      setStep('verify');
    } else {
      navigate('/');
    }
  };

  if (step === 'verify') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <Logo />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📧</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', marginBottom: '0.8rem' }}>
              Check your email
            </h2>
            <p style={{ color: 'var(--mid)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              We sent a confirmation link to <strong>{form.email}</strong>.
              Click the link to activate your account, then come back to sign in.
            </p>
            <Link to="/login" style={{
              display: 'block', background: 'var(--orange)', color: 'white',
              padding: '0.85rem', borderRadius: 8, textDecoration: 'none',
              fontWeight: 700, textAlign: 'center'
            }}>
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Logo />
        <h1 style={styles.title}>Create your account</h1>
        <p style={styles.sub}>
          Use the same email you provided during restaurant onboarding
        </p>

        <form onSubmit={handleRegister} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Restaurant name</label>
            <input
              type="text" value={form.restaurantName}
              onChange={f('restaurantName')} required
              placeholder="Pho House"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email address</label>
            <input
              type="email" value={form.email}
              onChange={f('email')} required
              placeholder="owner@restaurant.com"
              style={styles.input}
            />
            <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              Must match the email you used when registering with TableChat
            </span>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password" value={form.password}
              onChange={f('password')} required minLength={8}
              placeholder="At least 8 characters"
              style={styles.input}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm password</label>
            <input
              type="password" value={form.confirm}
              onChange={f('confirm')} required
              placeholder="Repeat password"
              style={styles.input}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--orange)', fontWeight: 600 }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: '2rem' }}>
      <svg width="38" height="38" viewBox="0 0 120 120">
        <defs>
          <linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E85D04"/>
            <stop offset="100%" stopColor="#F48C06"/>
          </linearGradient>
        </defs>
        <path d="M20 30 C20 20, 30 10, 50 10 L70 10 C90 10, 100 20, 100 30 L100 60 C100 70, 90 80, 70 80 L40 80 L20 95 L20 80 C20 80, 20 70, 20 60 Z" fill="url(#lg)" opacity="0.9"/>
        <rect x="40" y="35" width="40" height="4" fill="white" rx="2"/>
        <rect x="42" y="39" width="4" height="25" fill="white" rx="2"/>
        <rect x="74" y="39" width="4" height="25" fill="white" rx="2"/>
        <rect x="38" y="64" width="44" height="6" fill="white" rx="3"/>
        <circle cx="85" cy="25" r="8" fill="#0A6847"/>
        <path d="M85 20 L85 30 M80 25 L90 25" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <span style={{ fontFamily: 'Fraunces, serif', fontSize: '1.3rem', fontWeight: 900, color: 'var(--ink)' }}>
        Table<span style={{ color: 'var(--orange)' }}>Chat</span>
      </span>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' },
  card: { background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 700, textAlign: 'center', marginBottom: '0.4rem', color: 'var(--ink)' },
  sub: { textAlign: 'center', color: 'var(--mid)', fontSize: '0.85rem', marginBottom: '2rem', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: '1.1rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)' },
  input: { padding: '0.8rem 1rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.95rem', outline: 'none', color: 'var(--ink)', background: 'white' },
  error: { background: 'var(--red-bg)', color: 'var(--red-tx)', padding: '0.75rem 1rem', borderRadius: 8, fontSize: '0.875rem', fontWeight: 500 },
  btn: { background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.9rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.4rem' },
  footer: { textAlign: 'center', fontSize: '0.875rem', color: 'var(--mid)', marginTop: '1.5rem' },
};
